// ============================================================================
// src/app/data/queryClient.ts — the app's single QueryClient and its defaults
// ============================================================================
// PROJECT.md "Query identity and cache behavior" owns this contract:
// data is stale on arrival, retained for five minutes after its last observer,
// and refetched on mount. Scoped keys isolate users; AuthContext clears the
// cache on sign-out. routeMutationCatalog.ts declares write invalidations.
//
// Query also deduplicates in-flight reads and keeps current data visible during
// refetch. Automatic retries, focus/reconnect refetches and polling stay off;
// the defaults below make those choices explicit.
// ============================================================================

import {QueryClient} from '@tanstack/react-query';

// Exported so a unit test can assert the defaults rather than trusting a
// comment. Changing these options changes runtime behavior.
export const QUERY_PARITY_DEFAULTS = Object.freeze({
  queries: Object.freeze({
    // Query default: 0. Kept. Data is considered stale the moment it arrives,
    // so a mount always refetches — which is exactly "freshness is a property
    // of remounting". This is the one Query default that already matches.
    staleTime: 0,

    // Query default: 5 * 60 * 1000 (five minutes). THIS IS THE IMPORTANT ONE,
    // and as of this commit it is DELIBERATELY ON — Query's own default value,
    // adopted on purpose rather than inherited.
    //
    // gcTime is how long a cache entry survives after its last observer goes
    // away. With it on, navigating hub -> record -> hub paints the hub's rows
    // INSTANTLY (loading false, data present) and refreshes underneath, because
    // staleTime stays 0 so the data is stale on arrival and every mount still
    // refetches. That pairing IS the decision: stale-while-revalidate. Instant
    // paint from cache, correctness restored a moment later.
    //
    // What changes for a user: the first frame of a back-navigation shows the
    // previous rows instead of a loading state. On farm wifi that is the whole
    // point — the spinner was never protecting anything, it was just latency
    // made visible.
    //
    // THE SECURITY CONDITION THIS CREATES. The previous value was zero partly
    // because retention raises a question that no-retention answers for free:
    // the keys carry the full subject scope, so one user's entry can never be
    // READ under another's key — but with retention on, a signed-out user's
    // rows sit in memory on a shared browser until collection. Farm devices are
    // shared. The old comment here predicted that a sign-out cache-clear hook
    // "is needed and none can be forgotten" — so it is added in the same commit
    // that creates the need, in AuthContext.signOut, and pinned by a test.
    // Raising this value WITHOUT that hook is the mistake this paragraph exists
    // to prevent.
    gcTime: 5 * 60 * 1000,

    // Query default: 3 retries with exponential backoff. Today a failed read
    // surfaces its error immediately and the surface offers a Retry button.
    // Three silent retries would delay that banner by seconds while the user
    // stares at a spinner, and would triple the load on a database that is
    // already failing. Retry stays a user decision.
    retry: false,

    // Query default: true. Required with retained, immediately stale data:
    // a returning route paints its cached rows and refreshes them on mount.
    refetchOnMount: true,

    // Query default: TRUE. Every alt-tab back to the browser would fire the
    // surface's full read fan-out — seven queries on the cattle record page.
    // The app has never done that. On farm wifi it is a real cost, and a
    // background refetch that lands mid-edit is the same class of surprise as
    // the keystroke-eating defect this lane is fixing.
    refetchOnWindowFocus: false,

    // Query default: TRUE. Same reasoning. Rural connections flap; a reconnect
    // storm of refetches is new behaviour, not preserved behaviour.
    refetchOnReconnect: false,

    // Query default: false. Stated rather than inherited, because polling is a
    // continuous network cost that should never appear by accident.
    refetchInterval: false,

    // Query default: 'online'. MUST BE OVERRIDDEN, and this one is not cosmetic.
    //
    // Under 'online', a query started while navigator.onLine is false does not
    // run at all: it sits in fetchStatus 'paused' with isPending true, forever,
    // and the queryFn is never called. Verified against 5.101.4, not assumed.
    // The surface would show its spinner with no error and no Retry — a hang
    // where today there is a failure message.
    //
    // Today's runtime fires regardless and lets the request fail, which is the
    // behaviour to preserve. It is also the right one here: navigator.onLine is
    // unreliable (it reports "online" for a captive portal and can lag a real
    // reconnection), and this app already has its own offline handling in the
    // submit path. Query must not second-guess the network.
    networkMode: 'always',

    // Query default: TRUE — on a refetch, values that are deep-equal to the
    // previous result keep their old object references.
    //
    // Turned OFF for parity. Today every settle produces brand-new objects, so
    // every consumer re-renders on every refetch. Structural sharing would keep
    // references stable and skip those renders, which is almost certainly an
    // improvement — but it changes referential identity across the whole
    // 3,400-line cattle hub, and "almost certainly" is not a standard for a
    // foundation commit. Turning it on is a one-line, separately measurable
    // change once there is somewhere to measure it.
    structuralSharing: false,

    // Query default: false. Kept. Errors are returned in `error` for the
    // surface's own banner; they must not escape to the root ErrorBoundary,
    // which would replace the whole app with a crash screen for one failed read.
    throwOnError: false,
  }),

  // No mutation goes through Query yet. These are set anyway so the FIRST one
  // that does inherits this lane's parity posture rather than Query's retry and
  // offline-pausing defaults, which is a decision that should be made on
  // purpose rather than discovered in production.
  mutations: Object.freeze({
    retry: false,
    networkMode: 'always',
  }),
});

// QUERY_PARITY_DEFAULTS is deliberately left un-annotated. Options in it are
// read as RAW SOURCE TEXT by static guards, so the object literal's wording is
// load-bearing in a way a type annotation cannot help with, and restructuring
// to satisfy a type risks moving a pinned token.
//
// The retention values are NOT spelled out in this comment on purpose. An
// earlier draft named them, which was wrong twice over: PR #118 changed what
// the guards assert, so the comment went stale immediately — and a guard that
// matches a value against raw source would have been satisfied by the COMMENT
// while the live setting said something else. Read the literal below, and read
// queryClient.test.ts, which asserts the behaviour rather than the text.
export function createAppQueryClient(): QueryClient {
  return new QueryClient({defaultOptions: QUERY_PARITY_DEFAULTS});
}

// A module singleton rather than component state.
//
// AppProviders is documented as composition-only and holds no state of its own;
// giving it a useState just to own this instance would be the first crack in
// that. A singleton is also what lets non-component code — a mutation helper
// invalidating a key, a prefetch on hover — reach the same cache later without
// threading it through props.
//
// Constructing a QueryClient is inert: it starts no timers and touches no
// browser API until QueryClientProvider mounts it. Importing this module in a
// node test is safe.
export const appQueryClient = createAppQueryClient();
